import React, { useState, useEffect, KeyboardEvent } from 'react';
import DeleteIcon from './delete-icon.ui';

import styles from './tag-selector.module.css';
import classNames from 'classnames';

function isValidValue(value: string) {
  return /^[a-zA-Z0-9+#.\-_\s]+$/.test(value);
}

type ValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

function validateCustomTag(
  customTag: string,
  canSelectMore: boolean,
  optimisticSelected: string[],
  selectedTags: string[],
): ValidationResult {
  if (!customTag) {
    return { isValid: false };
  }

  if (!canSelectMore) {
    return { isValid: false };
  }

  if (optimisticSelected.includes(customTag) || selectedTags.includes(customTag)) {
    return { isValid: false };
  }

  if (!isValidValue(customTag)) {
    return {
      isValid: false,
      errorMessage: 'Invalid characters. Please use letters, numbers, and common symbols only.',
    };
  }

  return { isValid: true };
}

type TagSelectorUiProps = {
  tags: string[];
  selectedTags?: string[];
  loading?: boolean;
  error?: string | null;
  maxSelection?: number;
  placeholder?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
}

const TagSelectorUi = (props: TagSelectorUiProps) => {
  const {
    tags,
    selectedTags = [],
    loading,
    placeholder = 'Select tags',
    error,
    allowCustom,
    maxSelection,
    disabled,
    className,
    onAddTag,
    onRemoveTag,
  } = props;

  const [optimisticSelected, setOptimisticSelected] = useState(selectedTags);
  const [customSkillValue, setCustomSkillValue] = useState('');
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Fix: Use optimisticSelected for canSelectMore calculation
  const canSelectMore = !maxSelection || optimisticSelected.length < maxSelection;

  useEffect(() => {
    if (justAdded && selectedTags.includes(justAdded)) {
      setJustAdded(null);
      return;
    }

    setOptimisticSelected(selectedTags);
  }, [selectedTags, justAdded, optimisticSelected]);

  const handlePredefinedTagClick = (tagName: string) => {
    if (disabled) return;

    if (optimisticSelected.includes(tagName)) {
      setOptimisticSelected(prev => prev.filter(tag => tag !== tagName));
      onRemoveTag(tagName);
    } else if (canSelectMore) {
      setOptimisticSelected(prev => [...prev, tagName]);
      onAddTag(tagName);
    }
  };

  const handlePredefinedTagKeyDown = (e: KeyboardEvent<HTMLButtonElement>, tagName: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handlePredefinedTagClick(tagName);
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setOptimisticSelected(prev => prev.filter(tag => tag !== skill));
    onRemoveTag(skill);
  };

  const addCustomTag = (customTag: string) => {
    const validation = validateCustomTag(customTag, canSelectMore, optimisticSelected, selectedTags);

    if (!validation.isValid) {
      if (validation.errorMessage) {
        setErrorMessage(validation.errorMessage);
        return;
      }
      if (customTag) {
        setCustomSkillValue('');
      }
      return;
    }

    setOptimisticSelected(prev => [...prev, customTag]);
    setJustAdded(customTag);
    onAddTag(customTag);
    setCustomSkillValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addCustomTag(e.currentTarget.value.trim());
    }
  };

  const handleBlur = () => {
    addCustomTag(customSkillValue.trim());
  };

  if (loading) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  // Filter custom skills from optimisticSelected (skills not in predefined tags)
  const customSkills = optimisticSelected.filter(skill => !tags.includes(skill));

  return (
    <div className={`${styles.container} ${className}`}>
      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((tag, index) => {
            const isSelected = optimisticSelected?.includes(tag);
            const isDisabled = disabled || (!isSelected && !canSelectMore);

            return (
              <button
                key={tag + index}
                type="button"
                onClick={() => handlePredefinedTagClick(tag)}
                onKeyDown={(e) => handlePredefinedTagKeyDown(e, tag)}
                disabled={isDisabled}
                className={classNames(
                  styles.tagButton,
                  isSelected && styles.selected,
                  isDisabled && styles.disabled,
                )}
              >
                {tag}
              </button>
            );
          })}
        </div>
      )}

      {allowCustom && (
        <div className={styles.inputContainer}>
          <input
            id={customSkillValue}
            type="text"
            value={customSkillValue}
            onChange={(e) => {
              setCustomSkillValue(e.target.value);
              setErrorMessage('');
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={30}
            className={styles.input}
          />

          {customSkills.length > 0 && (
            <div className={styles.skillsContainer}>
              {customSkills.map((skill, index) => (
                <div key={index} className={styles.chip}>
                  <div className={styles.text}>{skill}</div>
                  <DeleteIcon onClick={() => handleRemoveSkill(skill)} color={'var(--white-color'}/>
                </div>
              ))}
            </div>
          )}

          {errorMessage && <div className={styles.error}>{errorMessage}</div>}
          {error && <div className={styles.error}>{error}</div>}
        </div>
      )}
    </div>
  );
};

export default TagSelectorUi;
